import React, { useState } from 'react';
import { Box, Button, Divider, IconButton, List, ListItem, ListItemButton, ListItemText, Menu, MenuItem, Slider, Stack, TextField, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/AddOutlined';
import type { BrandAsset, BrandKit } from '../../../services/editingApi';
import type { EditList, Overlay, TextOverlay, TextStyle } from '../../../utils/editing/editList';
import { EDIT_LIMITS, FONT_FAMILIES, outputDurationMs } from '../../../utils/editing/editList';
import { addOverlay, addText, defaultPosition, formatMs, removeItem, updateItem } from '../editStudioModel';
import { Choice, PanelHint, PositionPicker, SecondsField } from './controls';

interface LooksPanelProps {
  list: EditList;
  kit: BrandKit | null;
  outputMs: number;
  onChange: (next: EditList) => void;
  onOpenBrandKit: () => void;
}

const TEXT_STYLE_LABELS: Record<TextStyle, string> = { title: 'Title', lowerThird: 'Name (lower third)', plain: 'Plain text' };
const FONT_LABELS: Record<string, string> = { sans: 'Clean (sans)', serif: 'Classic (serif)', mono: 'Typewriter (mono)' };

/** Pictures (the logo) and text (titles, names, lines) on the picture. */
const LooksPanel: React.FC<LooksPanelProps> = ({ list, kit, outputMs, onChange, onOpenBrandKit }) => {
  const [selectedOverlay, setSelectedOverlay] = useState<string | null>(null);
  const [selectedText, setSelectedText] = useState<string | null>(null);
  const [pictureMenu, setPictureMenu] = useState<HTMLElement | null>(null);
  const [textMenu, setTextMenu] = useState<HTMLElement | null>(null);
  const total = outputDurationMs(list);
  const images: BrandAsset[] = (kit?.assets || []).filter((asset) => asset.kind === 'image');
  const overlay = list.overlays.find((item) => item.id === selectedOverlay) || null;
  const text = list.texts.find((item) => item.id === selectedText) || null;
  const assetName = (id: string) => images.find((asset) => asset.id === id)?.name || 'Picture';

  const changeOverlay = (change: Partial<Overlay>) => { if (overlay) onChange({ ...list, overlays: updateItem(list.overlays, overlay.id, change) }); };
  const changeText = (change: Partial<TextOverlay>) => { if (text) onChange({ ...list, texts: updateItem(list.texts, text.id, change) }); };

  const addPicture = (asset: BrandAsset) => {
    const isLogo = kit?.logoAssetId === asset.id;
    const next = addOverlay(list, asset.id, defaultPosition(isLogo ? 'top-right' : 'center', isLogo ? 3 : 0));
    onChange(next);
    setSelectedOverlay(next.overlays[next.overlays.length - 1]?.id ?? null);
    setPictureMenu(null);
  };
  const addLine = (style: TextStyle) => {
    const name = kit?.displayName || 'Your name';
    const next = addText(list, style, style === 'lowerThird' ? name : style === 'title' ? 'Title' : 'Text', outputMs, { color: '#FFFFFF' });
    onChange(next);
    setSelectedText(next.texts[next.texts.length - 1]?.id ?? null);
    setTextMenu(null);
  };

  return (
    <Box>
      <Typography variant="subtitle2">Pictures</Typography>
      <PanelHint>Your logo or any picture from your brand kit, where and when you want it.</PanelHint>
      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} disabled={list.overlays.length >= EDIT_LIMITS.maxOverlays} onClick={(event) => (images.length ? setPictureMenu(event.currentTarget) : onOpenBrandKit())} data-testid="add-picture">
          {images.length ? 'Add a picture' : 'Upload a logo first'}
        </Button>
        <Button size="small" onClick={onOpenBrandKit}>Brand kit</Button>
      </Stack>
      <Menu open={Boolean(pictureMenu)} anchorEl={pictureMenu} onClose={() => setPictureMenu(null)}>
        {images.map((asset) => (
          <MenuItem key={asset.id} onClick={() => addPicture(asset)}>
            <img src={asset.url} alt="" style={{ width: 28, height: 28, objectFit: 'contain', marginRight: 8 }} />
            {asset.name}{kit?.logoAssetId === asset.id ? ' (logo)' : ''}
          </MenuItem>
        ))}
      </Menu>
      <List dense disablePadding>
        {list.overlays.map((item) => (
          <ListItem key={item.id} disableGutters secondaryAction={<IconButton size="small" aria-label={`Remove ${assetName(item.assetId)}`} onClick={() => { onChange({ ...list, overlays: removeItem(list.overlays, item.id) }); if (selectedOverlay === item.id) setSelectedOverlay(null); }}><DeleteOutlineIcon fontSize="inherit" /></IconButton>}>
            <ListItemButton selected={item.id === selectedOverlay} onClick={() => setSelectedOverlay(item.id)} sx={{ borderRadius: 1 }}>
              <ListItemText primary={assetName(item.assetId)} secondary={item.fromMs === null && item.toMs === null ? 'Whole video' : `${formatMs(item.fromMs ?? 0)} – ${formatMs(item.toMs ?? total)}`} primaryTypographyProps={{ variant: 'body2' }} secondaryTypographyProps={{ variant: 'caption' }} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      {overlay ? (
        <Stack spacing={1.5} sx={{ mt: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }} data-testid="picture-settings">
          <PositionPicker value={overlay.position} onChange={(position) => changeOverlay({ position })} />
          <Box>
            <Typography variant="caption" color="text.secondary">Size: {Math.round(overlay.widthPct)}% of the width</Typography>
            <Slider size="small" min={EDIT_LIMITS.overlayWidthPct.min} max={60} step={1} value={overlay.widthPct} onChange={(_, v) => changeOverlay({ widthPct: v as number })} aria-label="Picture size" />
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Opacity: {Math.round(overlay.opacity * 100)}%</Typography>
            <Slider size="small" min={0.1} max={1} step={0.05} value={overlay.opacity} onChange={(_, v) => changeOverlay({ opacity: v as number })} aria-label="Picture opacity" />
          </Box>
          <Stack direction="row" spacing={1}>
            <SecondsField label="From" valueMs={overlay.fromMs} allowEmpty maxMs={total} onChange={(fromMs) => changeOverlay({ fromMs, toMs: overlay.toMs !== null && fromMs !== null && overlay.toMs <= fromMs ? null : overlay.toMs })} />
            <SecondsField label="To" valueMs={overlay.toMs} allowEmpty maxMs={total} onChange={(toMs) => changeOverlay({ toMs: toMs !== null && overlay.fromMs !== null && toMs <= overlay.fromMs ? null : toMs })} />
          </Stack>
        </Stack>
      ) : null}

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2">Text</Typography>
      <PanelHint>A title at the start, your name as a lower third, or a line anywhere. Added at the playhead.</PanelHint>
      <Button size="small" variant="outlined" startIcon={<AddIcon />} disabled={list.texts.length >= EDIT_LIMITS.maxTexts} onClick={(event) => setTextMenu(event.currentTarget)} data-testid="add-text">Add text</Button>
      <Menu open={Boolean(textMenu)} anchorEl={textMenu} onClose={() => setTextMenu(null)}>
        {(['title', 'lowerThird', 'plain'] as TextStyle[]).map((style) => (
          <MenuItem key={style} onClick={() => addLine(style)}>{TEXT_STYLE_LABELS[style]}</MenuItem>
        ))}
      </Menu>
      <List dense disablePadding sx={{ mt: 1 }}>
        {list.texts.map((item) => (
          <ListItem key={item.id} disableGutters secondaryAction={<IconButton size="small" aria-label={`Remove text ${item.text}`} onClick={() => { onChange({ ...list, texts: removeItem(list.texts, item.id) }); if (selectedText === item.id) setSelectedText(null); }}><DeleteOutlineIcon fontSize="inherit" /></IconButton>}>
            <ListItemButton selected={item.id === selectedText} onClick={() => setSelectedText(item.id)} sx={{ borderRadius: 1 }}>
              <ListItemText primary={item.text || '(empty)'} secondary={`${TEXT_STYLE_LABELS[item.style]} · ${formatMs(item.fromMs)} – ${formatMs(item.toMs)}`} primaryTypographyProps={{ variant: 'body2', noWrap: true }} secondaryTypographyProps={{ variant: 'caption' }} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      {text ? (
        <Stack spacing={1.5} sx={{ mt: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }} data-testid="text-settings">
          <TextField size="small" label="Text" value={text.text} inputProps={{ maxLength: EDIT_LIMITS.maxTextLength, 'data-testid': 'text-content' }} onChange={(event) => changeText({ text: event.target.value })} multiline maxRows={3} />
          {text.style === 'lowerThird' ? (
            <TextField size="small" label="Second line (role, handle)" value={text.secondaryText || ''} inputProps={{ maxLength: EDIT_LIMITS.maxTextLength }} onChange={(event) => changeText({ secondaryText: event.target.value || null })} />
          ) : null}
          <Stack direction="row" spacing={1}>
            <Choice label="Font" value={text.font} options={FONT_FAMILIES.map((font) => ({ value: font, label: FONT_LABELS[font] }))} onChange={(font) => changeText({ font })} />
            <TextField size="small" label="Colour" type="color" value={text.color.slice(0, 7)} onChange={(event) => changeText({ color: event.target.value.toUpperCase() })} sx={{ width: 90 }} inputProps={{ 'aria-label': 'Text colour' }} />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary">Background</Typography>
            <Button size="small" variant={text.backgroundColor ? 'outlined' : 'contained'} onClick={() => changeText({ backgroundColor: null })}>None</Button>
            <Button size="small" variant={text.backgroundColor ? 'contained' : 'outlined'} onClick={() => changeText({ backgroundColor: text.backgroundColor || '#000000A0' })}>Dark box</Button>
          </Stack>
          <PositionPicker value={text.position} onChange={(position) => changeText({ position })} withMargin={text.style !== 'title'} />
          <Box>
            <Typography variant="caption" color="text.secondary">Size: {text.sizePct}% of the height</Typography>
            <Slider size="small" min={EDIT_LIMITS.textSizePct.min} max={EDIT_LIMITS.textSizePct.max} step={0.5} value={text.sizePct} onChange={(_, v) => changeText({ sizePct: v as number })} aria-label="Text size" />
          </Box>
          <Stack direction="row" spacing={1}>
            <SecondsField label="From" valueMs={text.fromMs} maxMs={total} onChange={(fromMs) => { if (fromMs !== null && fromMs < text.toMs) changeText({ fromMs }); }} />
            <SecondsField label="To" valueMs={text.toMs} maxMs={total} onChange={(toMs) => { if (toMs !== null && toMs > text.fromMs) changeText({ toMs }); }} />
            <SecondsField label="Fade" valueMs={text.fadeMs} maxMs={EDIT_LIMITS.maxFadeMs} onChange={(fadeMs) => { if (fadeMs !== null) changeText({ fadeMs }); }} />
          </Stack>
        </Stack>
      ) : null}
    </Box>
  );
};

export default LooksPanel;
